<template>
  <LayoutOverlay class="modal-upload-files" @closeOverlay="closeModal()">
    <div class="container">
      <div class="header">
        <h3 class="header__title">
          {{ t('general__data__file-upload-v2') }}
        </h3>
        <button v-if="!filesUpload.length" class="header__close-button" @click="closeModal()">
          <IconXCircleMedium class="header__close-button__icon" />
        </button>
      </div>
      <div class="content">
        <p class="content__description">
          {{ descriptionText }}
        </p>
        <InputFileDrop
          v-slot="{ dropZoneActive }"
          class="content__drop-zone"
          tabindex="0"
          @files-dropped="addFiles"
          @keydown.enter="clickFileInput"
          @keydown.space="(event: Event) => clickFileInput(event, true)"
        >
          <label for="file-input">
            <span v-if="dropZoneActive">
              <span class="content__drop-zone__add-file__text">{{
                t('general__data__file-drop')
              }}</span>
            </span>
            <span v-else>
              <span class="content__drop-zone__add-file__icon">
                <IconUpload />
              </span>
              <span class="content__drop-zone__add-file__text">
                <span class="content__drop-zone__add-file__text__highlight">
                  {{ t('general__data__file-add') }}
                </span>
                {{ t('general__data__file-drop-or').toLowerCase() }}
                <br />
                {{ t('general__data__upload-will-start').toLowerCase() }}
              </span>
            </span>
            <input id="file-input" ref="fileInput" type="file" multiple @change="onInputChange" />
          </label>
        </InputFileDrop>
        <ul v-if="filesUpload.length" class="content__file-list">
          <li
            v-for="file of filesUpload"
            :key="file.id"
            class="content__file-list__row"
            :class="{ 'content__file-list__row__error': file.status === false }"
            :file="file"
          >
            <IconPaperclip class="content__file-list__row__icon" />
            <span class="content__file-list__row__text">{{ file.file.name }}</span>
            <span class="content__file-list__row__status">
              <div
                v-if="file.status === 'loading'"
                class="content__file-list__row__status__loading-spinner"
              >
                <PulseLoader color="#3f46d6" size="8px" />
              </div>
              <IconCheck
                v-if="file.status === true"
                class="content__file-list__row__status__icon-success"
              />
              <button
                v-if="file.status === false"
                class="content__file-list__row__status__button"
                aria-label="Remove failed file"
                @click="removeFile(file)"
              >
                <IconX class="content__file-list__row__status__button__icon-error" />
              </button>
            </span>
          </li>
        </ul>
      </div>
      <div v-if="filesUpload.length" class="footer">
        <ButtonPrimary
          class="footer__button"
          :buttonText="t('general__text__done')"
          :disabled="isUploadingFiles"
          @onClicked="closeModal()"
        />
      </div>
    </div>
  </LayoutOverlay>
</template>
<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useGeneralStore } from '@/pinia'

import ButtonPrimary from '@/design-system/atoms/buttons/ButtonPrimary.vue'
import IconCheck from '@/design-system/assets/icons/IconCheck.vue'
import IconPaperclip from '@/design-system/assets/icons/IconPaperclip.vue'
import IconUpload from '@/design-system/assets/icons/IconUpload.vue'
import IconX from '@/design-system/assets/icons/IconX.vue'
import IconXCircleMedium from '@/design-system/assets/icons/IconXCircleMedium.vue'
import InputFileDrop from '@/design-system/atoms/InputFileDrop.vue'
import LayoutOverlay from '@/design-system/templates/LayoutOverlay.vue'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import useFileList from '@/hooks/attachments/useFileList'
import createFileUploader from '@/hooks/attachments/useFileUploader'

// emits
interface EMITS {
  (e: 'closeUploadFilesModal', val: boolean)
}
const emits = defineEmits<EMITS>()

// variables
const { t } = useI18n()
const piniaStore = useGeneralStore()
const { filesUpload, addFiles, removeFile } = useFileList()
const { uploadFiles } = createFileUploader()

const fileInput = ref<HTMLInputElement | null>(null)

// computed
const selectedTicket = computed(() => piniaStore.selectedTicket)

const descriptionText = computed<string>(() => {
  let description = ''
  if (!selectedTicket.value) return description
  description = `${t('message__files-upload__part1')} ${t(
    'message__files-upload__part2-connection'
  )} "${selectedTicket.value.title}".`

  return description
})
const isUploadingFiles = computed(() => {
  return filesUpload.value.some((file) => file.status === 'loading' || file.status === null)
})

// methods
function clickFileInput(event?: Event, preventDefault?: boolean) {
  if (event && preventDefault) event.preventDefault() // Prevent space bar scrolling
  if (fileInput.value) fileInput.value.click()
}
function closeModal() {
  piniaStore.setFilesUploaded(null)
  emits('closeUploadFilesModal', false)
}
function onInputChange(event) {
  addFiles(event.target.files)
  event.target.value = null
}

// watches
watch(filesUpload, async () => {
  if (filesUpload.value.length && selectedTicket.value) {
    await uploadFiles(filesUpload.value, selectedTicket.value.id, 'TICKET')
  }
  // Add files to pinia store for refreshing list in FilesDashboard
  let uploadedFilesArray: string[] = []
  filesUpload.value.forEach((file) => {
    if (file.status === true) uploadedFilesArray.push(file.id)
  })
  piniaStore.setFilesUploaded(uploadedFilesArray)
})
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins.scss';
@use '@/design-system/design-tokens/mixins.scss';

.modal-upload-files {
  display: flex;
  justify-content: center;
}
.container {
  background-color: variables.$color-pure-white;
  border-radius: variables.$border-radius-small;
  display: flex;
  flex-direction: column;
  max-height: calc(#{100%} - 72px);
  margin: auto auto 32px auto;
  padding: 24px;
  position: absolute;
  top: 32px;
  width: 544px;
  .content {
    height: 100%;
    margin: 0 -4px;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 0 4px;
    @include mixins.scrollbar-vertical;
    &__description {
      @include font-mixins.desktop-bold-paragraph;
      color: variables.$color-greyscale-content;
      margin: 0 0 24px 0;
    }
    &__create-act {
      display: flex;
      flex-direction: row;
      padding: 0;
      width: 100%;
      &__checkbox {
        border-radius: 0;
        flex-flow: nowrap;
        margin: 0 5px 0 -3px;
        overflow: hidden;
        padding: 0;
        position: unset;
        width: max-content;
      }
      &__info {
        height: 24px;
        margin: 0 auto 0 0;
        overflow: hidden;
        padding: 0;
        position: unset;
        width: 24px;
      }
    }
    &__create-act-selections {
      display: flex;
      flex-direction: column;
      gap: 8px;
      margin-top: 8px;
    }
    &__disabled-drop-zone {
      align-items: center;
      background-color: variables.$color-greyscale-background;
      border-radius: 12px;
      border: 1px dashed variables.$color-greyscale-secondary;
      display: flex;
      height: 120px;
      justify-content: center;
      margin: 16px 0;
      &:hover {
        cursor: not-allowed;
      }
      &__error-text {
        @include font-mixins.desktop-regular-paragraph;
        color: variables.$color-error;
      }
    }
    &__drop-zone {
      align-items: center;
      display: flex;
      justify-content: center;
      margin: 16px 0;
      #file-input {
        // Hide standard file input
        opacity: 0;
        z-index: -1;
        position: absolute;
      }
      &__add-file {
        &__icon {
          color: variables.$color-primary;
          cursor: pointer;
          padding-right: 8px;
        }
        &__text {
          @include font-mixins.desktop-bold-paragraph;
          color: variables.$color-greyscale-content;
          display: inline-block;
          vertical-align: top;
          &__highlight {
            color: variables.$color-primary;
            cursor: pointer;
          }
        }
      }
    }
    &__file-list {
      display: block;
      list-style-type: none;
      margin-block-end: 16px;
      margin-block-start: 0;
      max-height: 184px;
      overflow-x: hidden;
      overflow-y: auto;
      padding-inline-end: 0;
      padding-inline-start: 0;
      @include mixins.scrollbar-vertical;
      &__row {
        align-items: center;
        background-color: variables.$color-greyscale-background;
        border-radius: 12px;
        display: flex;
        gap: 8px;
        height: 48px;
        margin-bottom: 8px;
        padding: 0 16px;
        &:last-child {
          margin-bottom: 0;
        }
        &__error {
          background-color: #fff1f0;
        }
        &__icon {
          color: variables.$color-greyscale-content;
          height: 24px;
          min-width: 24px;
        }
        &__status {
          display: flex;
          justify-items: flex-end;
          margin-left: auto;
          &__button {
            all: unset;
            &:focus {
              outline: revert;
            }
            height: 24px;
            width: 24px;
            &__icon-error {
              color: variables.$color-error;
              cursor: pointer;
              height: 24px;
              min-width: 24px;
            }
          }
          &__icon-success {
            color: variables.$color-success;
            height: 24px;
            min-width: 24px;
          }
          &__loading-spinner {
            min-width: 36px;
          }
        }
        &__text {
          @include font-mixins.desktop-bold-paragraph;
          color: variables.$color-greyscale-content;
          display: inline-block;
          overflow: hidden;
          text-overflow: ellipsis;
          vertical-align: top;
          white-space: nowrap;
        }
      }
    }
  }
  .footer {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    padding: 16px 0;
    &__button {
      width: 100%;
      &__cancel {
        color: variables.$color-primary;
      }
    }
  }
  .header {
    display: flex;
    justify-content: space-between;
    &__close-button {
      all: unset;
      height: 40px;
      width: 40px;
      &:focus {
        outline: revert;
      }
      &__icon {
        color: variables.$color-primary;
        cursor: pointer;
      }
    }
    &__title {
      margin: 8px 0;
      @include font-mixins.desktop-bold-h4;
    }
  }
}
.spinner {
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
}
@media screen and (max-width: 768px) {
  .container {
    max-width: 80%;
  }
}
</style>
